import { CommonModule, formatDate } from '@angular/common';
import { Component, Inject, Input, LOCALE_ID, NgModule, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Utilities } from '@app/common/utilities';
import { ActionButtonModule } from '@app/components/actions/action-button/action-button.component';
import { ActionLinkModule } from '@app/components/actions/action-link/action-link.component';
import { TextPlaceholderModule } from '@app/components/text-placeholder/text-placeholder.component';
import { MyEvent, Response } from '@app/models/my-event';
import { MyTask, TaskStatus, TaskType } from '@app/models/my-task';
import { LinkModule } from '@app/directives/link.directive';
import { SafeModule } from '@app/pipes/safe.pipe';
import { EventsService } from '@app/services/events.service';
import { OutlookService } from '@app/services/outlook.service';
import { PlannerService } from '@app/services/planner.service';
import { UserService } from '@app/services/user.service';
import { Environment } from '@environments/interface.environment';
import { Event, PlannerTask } from '@microsoft/microsoft-graph-types';
import { OutlookTask, OutlookTaskFolder } from '@microsoft/microsoft-graph-types-beta';
import { addDays, isBefore, isEqual, isSameDay, parseISO, startOfDay } from 'date-fns';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { catchError, concatMap, mergeMap, tap } from 'rxjs/operators';
import { FormatDateModule } from '@app/pipes/format-date.pipe';

@Component({
  selector: 'app-my-day',
  templateUrl: './my-day.component.html',
  styleUrls: ['./my-day.component.scss']
})
export class MyDayComponent implements OnInit, OnDestroy {

  subscription: Subscription;
  userPhotos$: Observable<Array<string>>;

  completeSubScription: Subscription;
  responseSubscription: Subscription;

  taskFolders: Array<OutlookTaskFolder>;
  defaultTaskFolderId: string;
  tasks: Array<MyTask>;
  events: Array<MyEvent>;

  todoLink = 'https://to-do.office.com/tasks';
  plannerLink = 'https://tasks.office.com';
  calendarLink = 'https://outlook.office.com/owa/?path=/calendar';

  today: string;
  todayMidnight: Date;
  tenantName: string;
  domain: string;

  tasksCount = 0;
  eventsCount = 0;
  showLessTasksCount = 0;

  @Input() minTasks = 5;
  @Input() showMore = 5;
  @Input() showMoreInterval = 5;
  @Input() tasksToRequestFromApi = 100;

  @ViewChildren(MatExpansionPanel) accordion: QueryList<MatExpansionPanel>;

  constructor(@Inject(LOCALE_ID) public localeId: string,
              @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private plannerService: PlannerService,
              private outlookService: OutlookService,
              private userService: UserService,
              private eventService: EventsService) { }

  ngOnInit() {
    this.tasks = [];
    this.events = [];
    this.today = formatDate(new Date(), 'mediumDate', this.localeId);
    this.tenantName = this.environment.tenantHostUrl.replace(/(^\w+:|^)\/\//, '').split('.')[0];
    this.domain = this.tenantName + '.onmicrosoft.com';

    const currentDate = new Date();
    this.todayMidnight = startOfDay(currentDate);
    const tomorrowMidnight = startOfDay(addDays(currentDate, 1));
    const nextWeek = addDays(this.todayMidnight, 7);

    this.subscription = this.outlookService
      .getMyOutlookTaskFolders()
      .pipe(
        mergeMap(taskFolders => {
          this.taskFolders = taskFolders;
          this.defaultTaskFolderId = taskFolders.filter(item => item.isDefaultFolder === true)[0].id;
          const taskFolderIds = this.taskFolders.map(folder => folder.id);
          return combineLatest(
            [this.plannerService.getMyPlannerTasks(this.tasksToRequestFromApi),
             this.outlookService.getMyOutlookTasksFromFoldersBatch(taskFolderIds, this.tasksToRequestFromApi),
             this.eventService.getMyEvents(100, this.todayMidnight, tomorrowMidnight)]
          );
        }))
      .subscribe(
        ([plannerResults, outlookResults, eventsResult]) => {

          const filteredEvents = eventsResult.filter(event => {
            const endDateUTC = parseISO(event.end.dateTime);
            return !isEqual(endDateUTC, this.todayMidnight);
          });

          this.eventsCount = filteredEvents.length;
          this.tasks = this.parseTasks(plannerResults, outlookResults, currentDate, nextWeek);
          this.events = filteredEvents.map(event => this.parseEvent(event));
        });
  }

  parseTasks(plannerResults: Array<PlannerTask>, outlookResults: Array<OutlookTask>, currentDate: Date, nextWeek: Date) {
    const plannerTasks = plannerResults && plannerResults.map ? plannerResults.map(task => {
      return this.fromPlanner(task, this.localeId, this.domain);
    }) : [];

    const outlookTasks = outlookResults && outlookResults.map ? outlookResults.map(task => {
      return MyTask.fromOutlook(task, this.localeId, this.defaultTaskFolderId);
    }) : [];

    const filteredTasks = plannerTasks.concat(outlookTasks).filter(task => {
      // exclude tasks that are completed before today
      if (task.CompletedDate) {
        return isSameDay(task.CompletedDate, currentDate);
      } else if (task.DueDate) {
        return isBefore(task.DueDate, nextWeek);
      } else {
        return true;
      }
    });

    const todaysDueDateTasks = filteredTasks.filter(task => {
      return task.DueDate && isSameDay(task.DueDate, currentDate) || task.DueDate && isBefore(task.DueDate, this.todayMidnight);
    });

    if (todaysDueDateTasks.length > this.eventsCount) {
      this.showMore = todaysDueDateTasks.length > this.minTasks ? todaysDueDateTasks.length : this.minTasks;
    } else {
      this.showMore = this.eventsCount > this.minTasks ? this.eventsCount : this.minTasks;
    }

    this.showLessTasksCount = this.showMore;

    // sort tasks ascending on created date first, so that tasks without due date should be oldest task on top after tasks with due date.
    const sortedOnCreatedDate = filteredTasks.sort((a, b) => (a.Created < b.Created) ? -1 : ((b.Created < a.Created) ? 1 : 0));

    // sort tasks descending on due date, sort tasks with no due date last
    const sortedTasks = sortedOnCreatedDate.sort((a, b) => {
      if (a.DueDateFormatted.length === 0) { return 1; }
      if (b.DueDateFormatted.length === 0) { return -1; }
      return a.DueDate.getTime() - b.DueDate.getTime();
    });

    this.tasksCount = sortedTasks.length;
    return sortedTasks;
  }

  fromPlanner(item: PlannerTask, localeId: string, tenantName: string) {

    let status = TaskStatus.NOTSTARTED;
    if (item.percentComplete === 50) {
      status = TaskStatus.INPROGRESS;
    } else if (item.percentComplete === 100) {
      status = TaskStatus.COMPLETED;
    }

    return new MyTask({
      Type: TaskType.Planner,
      Body: '',
      Subject: item.title,
      Status: status,
      Owner: '',
      CompletedDate: item.completedDateTime ? parseISO(item.completedDateTime) : null,
      CompletedDateFormatted: item.completedDateTime ? formatDate(item.completedDateTime, 'mediumDate', localeId) : '',
      DueDate: item.dueDateTime ? parseISO(item.dueDateTime) : null,
      DueDateFormatted: item.dueDateTime ? formatDate(item.dueDateTime, 'mediumDate', localeId) : '',
      StartDate: item.startDateTime ? parseISO(item.startDateTime) : null,
      StartDateFormatted: item.startDateTime ? formatDate(item.startDateTime, 'shortTime', localeId) : '',
      Created: item.createdDateTime ? parseISO(item.createdDateTime) : null,
      CreatedDateFormatted: item.createdDateTime ? formatDate(item.createdDateTime, 'mediumDate', localeId) : '',
      Url: `https://tasks.office.com/${tenantName}/en-US/Home/Task/${item.id}?Type=TaskLink&Channel=Link`,
      Id: item.id,
      Etag: item['@odata.etag'],
      PlannerTaskDetails$: this.plannerService.getPlannerTaskDetails(item.id),
      PlannerPlan$: this.plannerService.getPlannerPlan(item.planId),
      PlannerBucket$: this.plannerService.getPlannerBucket(item.bucketId)
    });
  }

  parseEvent(event: Event) {
    const currentTime = new Date().getTime();
    const timeZoneSuffix = event.start.timeZone === 'UTC' ? 'Z' : '';
    const localStartDate = parseISO(event.start.dateTime + timeZoneSuffix);
    const localEndDate = parseISO(event.end.dateTime + timeZoneSuffix);
    const localEndDateFormatted = isSameDay(localStartDate, localEndDate) ? formatDate(localEndDate, 'shortTime', this.localeId) : formatDate(localEndDate, 'EEE, MMM d, ', this.localeId) + formatDate(localEndDate, 'shortTime', this.localeId);

    let attendees = event.attendees;
    if (!event.isOrganizer) {
      attendees = event.attendees.filter(a => a.emailAddress.address !== event.organizer.emailAddress.address);
    }
    const acceptedUpns = event.attendees.filter(a => {
      return a.status.response === 'accepted';
    }).map(a => a.emailAddress.address);

    if (acceptedUpns.indexOf(event.organizer.emailAddress.address) === -1) {
      acceptedUpns.push(event.organizer.emailAddress.address);
    }

    let userResponse = Response.None;
    if (event.responseStatus.response === 'tentativelyAccepted') { userResponse = Response.Tentative; }
    if (event.responseStatus.response === 'declined') { userResponse = Response.Decline; }
    if (event.responseStatus.response === 'accepted' || event.isOrganizer) { userResponse = Response.Accepted; }

    const responses = attendees.map(a => a.status.response);

    return new MyEvent({
      Id: event.id,
      Subject: event.subject,
      BodyPreview: event.bodyPreview,
      EventStartDate: localStartDate,
      EventStartDateFormatted: formatDate(localStartDate, 'shortTime', this.localeId),
      EventStartDateAllDayFormatted: formatDate(localStartDate, 'fullDate', this.localeId),
      EventEndDate: localEndDate,
      EventEndDateFormatted: localEndDateFormatted,
      Location: event.location.displayName,
      Recurring: event.type,
      WebLink: event.webLink,
      IsAllDay: event.isAllDay,
      Attendees: event.attendees.length,
      Accepted: acceptedUpns.length,
      Declined: responses.filter(r => r === 'declined').length,
      NoResponse: responses.filter(r => r === 'none').length,
      Tentative: responses.filter(r => r === 'tentativelyAccepted').length,
      Expired: localEndDate.getTime() < currentTime,
      IsCancelled: event.isCancelled,
      IsOrganizer: event.isOrganizer,
      Response: userResponse,
      UserPhotos$: this.userService.getBatchUserPhotoByUpn(acceptedUpns),
    });
  }

  completeTask(task: MyTask, index: number) {
    task.Status = TaskStatus.COMPLETED;
    this.outlookService.reloadGetOutlookTasks();

    if (task.Type === TaskType.Planner) {
      this.completeSubScription = this.plannerService.completePlannerTask(task).subscribe();
    } else if (task.Type === TaskType.Outlook) {
      this.completeSubScription = this.outlookService.completeOutlookTask(task).subscribe();
    }

    this.accordion.toArray()[index].close();
  }

  acceptEvent(event: MyEvent, index: number, events: Array<MyEvent>) {
    event.Response = Response.Accepted;
    this.responseSubscription = this.eventService.acceptEvent(event.Id).pipe(
      concatMap((response) => {
        return this.eventService.getEvent(event.Id).pipe(tap(updatedEvent => {
          if (updatedEvent) {
            // show updated event
            events[index] = this.parseEvent(updatedEvent);
          }
        }));
      }),
      catchError(err => {
        return this.eventService.getEvent(event.Id).pipe(tap(updatedEvent => {
          if (updatedEvent) {
            // show updated event
            events[index] = this.parseEvent(updatedEvent);
          }
        }));
      }))
      .subscribe();

    this.accordion.toArray()[this.showMore + index].close();
  }

  tentativeAcceptEvent(event: MyEvent, index: number, events: Array<MyEvent>) {
    event.Response = Response.Tentative;
    this.responseSubscription = this.eventService.tentativelyAcceptEvent(event.Id).pipe(
      concatMap((response) => {
        return this.eventService.getEvent(event.Id).pipe(tap(updatedEvent => {
          if (updatedEvent) {
            // show updated event
            events[index] = this.parseEvent(updatedEvent);
          }
        }));
      }),
      catchError(err => {
        return this.eventService.getEvent(event.Id).pipe(tap(updatedEvent => {
          if (updatedEvent) {
            // show updated event
            events[index] = this.parseEvent(updatedEvent);
          }
        }));
      }))
      .subscribe();

    this.accordion.toArray()[this.showMore + index].close();
  }

  declineEvent(event: MyEvent, index: number, events: Array<MyEvent>) {
    event.Response = Response.Decline;
    this.responseSubscription = this.eventService.declineEvent(event.Id).subscribe(result => {
      events.splice(index, 1);
    });
  }

  cancelEvent(event: MyEvent, index: number, events: Array<MyEvent>) {
    this.responseSubscription = this.eventService.cancelEvent(event.Id).subscribe(result => {
      events.splice(index, 1);
    });
  }

  deleteEvent(event: MyEvent, index: number, events: Array<MyEvent>) {
    this.responseSubscription = this.eventService.deleteEvent(event.Id).subscribe(result => {
      events.splice(index, 1);
    });
  }

  getPreferredNameLetters(displayName: string) {
    return Utilities.generateInitials(displayName);
  }

  getImageSpacing(index: number) {
    const styles = {};
    const spacing = 1.625 * index;
    styles['left'] = spacing + 'rem';
    styles['top'] = '0';
    return styles;
  }

  toggleShowMore() {
    this.showMore = this.showMore === this.showLessTasksCount ? this.tasksCount : this.showLessTasksCount;
  }

  ngOnDestroy(): void {
    this.completeSubScription && this.completeSubScription.unsubscribe();
    this.responseSubscription && this.responseSubscription.unsubscribe();
  }
}
@NgModule({
  declarations: [MyDayComponent],
  exports: [MyDayComponent],
  imports: [
    CommonModule,
    MatExpansionModule,
    MatButtonModule,
    ActionLinkModule,
    ActionButtonModule,
    TextPlaceholderModule,
    SafeModule,
    LinkModule,
    FormatDateModule
  ]
})
export class MyDayModule { }
